import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Chip,
  Button,
  Alert,
  CircularProgress,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  Switch,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import pandoraAdminService from '../../../services/pandoraAdminService';
import type { PandoraTaxonomy } from '../../../services/pandoraAdminService';

interface GroupedTaxonomy {
  domain: string;
  classes: {
    class_name: string;
    elements: PandoraTaxonomy[];
  }[];
}

const DOMAIN_COLORS: Record<string, 'primary' | 'success' | 'warning'> = {
  estrutural: 'primary',
  teorico: 'success',
  factual: 'warning',
};

function getDomainColor(domain: string): 'primary' | 'success' | 'warning' | 'default' {
  return DOMAIN_COLORS[domain.toLowerCase()] ?? 'default';
}

function groupTaxonomy(items: PandoraTaxonomy[]): GroupedTaxonomy[] {
  const byDomain = new Map<string, Map<string, PandoraTaxonomy[]>>();

  for (const item of items) {
    if (!byDomain.has(item.domain)) {
      byDomain.set(item.domain, new Map());
    }
    const byClass = byDomain.get(item.domain)!;
    if (!byClass.has(item.class_name)) {
      byClass.set(item.class_name, []);
    }
    byClass.get(item.class_name)!.push(item);
  }

  return Array.from(byDomain.entries()).map(([domain, byClass]) => ({
    domain,
    classes: Array.from(byClass.entries()).map(([class_name, elements]) => ({
      class_name,
      elements: elements.sort((a, b) =>
        (a.element_name ?? '').localeCompare(b.element_name ?? '')
      ),
    })),
  }));
}

export default function PandoraTaxonomyPage() {
  const [items, setItems] = useState<PandoraTaxonomy[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [seedLoading, setSeedLoading] = useState(false);
  const [updatingId, setUpdatingId] = useState<string | null>(null);

  const loadTaxonomy = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await pandoraAdminService.listTaxonomy();
      setItems(data);
    } catch (err: any) {
      setError(
        err?.response?.data?.detail || err?.message || 'Erro ao carregar taxonomia.'
      );
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadTaxonomy();
  }, []);

  const handleSeed = async () => {
    try {
      setSeedLoading(true);
      setError(null);
      await pandoraAdminService.seedTaxonomy();
      await loadTaxonomy();
    } catch (err: any) {
      setError(
        err?.response?.data?.detail || err?.message || 'Erro ao popular taxonomia.'
      );
    } finally {
      setSeedLoading(false);
    }
  };

  const handleToggleActive = async (id: string, isActive: boolean) => {
    try {
      setUpdatingId(id);
      await pandoraAdminService.updateTaxonomy(id, { is_active: !isActive });
      await loadTaxonomy();
    } catch (err: any) {
      setError(
        err?.response?.data?.detail || err?.message || 'Erro ao atualizar elemento.'
      );
    } finally {
      setUpdatingId(null);
    }
  };

  if (loading && items.length === 0) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight={400}>
        <CircularProgress />
      </Box>
    );
  }

  const grouped = groupTaxonomy(items);

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box>
          <Typography variant="h4" gutterBottom>
            Taxonomia
          </Typography>
          <Typography variant="body1" color="text.secondary">
            Domínio &gt; Classe &gt; Elementos. Gerencie tags e ative/desative elementos.
          </Typography>
        </Box>
        <Button
          variant="contained"
          onClick={handleSeed}
          disabled={seedLoading}
          startIcon={seedLoading ? <CircularProgress size={20} color="inherit" /> : null}
        >
          {seedLoading ? 'Populando...' : 'Seed Taxonomia'}
        </Button>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {grouped.length === 0 ? (
        <Alert severity="info">
          Taxonomia vazia. Clique em "Seed Taxonomia" para popular com os dados padrão.
        </Alert>
      ) : (
        <Box>
          {grouped.map(({ domain, classes }) => (
            <Accordion key={domain} defaultExpanded>
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Chip
                  label={domain}
                  color={getDomainColor(domain)}
                  size="small"
                  sx={{ mr: 2 }}
                />
                <Typography>
                  Domínio: {domain}
                </Typography>
              </AccordionSummary>
              <AccordionDetails>
                {classes.map(({ class_name, elements }) => (
                  <Box key={`${domain}-${class_name}`} sx={{ mb: 3 }}>
                    <Typography variant="subtitle1" sx={{ mb: 1, fontWeight: 600 }}>
                      Classe: {class_name}
                    </Typography>
                    <List disablePadding>
                      {elements.map((el) => (
                        <ListItem
                          key={el.id}
                          sx={{
                            borderBottom: '1px solid',
                            borderColor: 'divider',
                          }}
                        >
                          <ListItemText
                            primary={el.full_tag}
                            secondary={el.description ?? undefined}
                          />
                          <ListItemSecondaryAction>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              <Chip
                                label={el.is_active ? 'Ativo' : 'Inativo'}
                                color={el.is_active ? 'success' : 'default'}
                                size="small"
                                variant="outlined"
                              />
                              <Switch
                                checked={el.is_active}
                                disabled={updatingId === el.id}
                                onChange={() => handleToggleActive(el.id, el.is_active)}
                              />
                            </Box>
                          </ListItemSecondaryAction>
                        </ListItem>
                      ))}
                    </List>
                  </Box>
                ))}
              </AccordionDetails>
            </Accordion>
          ))}
        </Box>
      )}
    </Box>
  );
}
